/**
 * Push Notification Manager
 *
 * Handles browser push notification subscription, permission requests,
 * and service worker registration for push notifications.
 */

import { apiClient } from './api-client.js';

/**
 * Push notification support status
 */
export interface PushSupport {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  subscription?: PushSubscription;
}

/**
 * Push notification manager class
 */
class PushNotificationManager {
  private serviceWorker: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;
  private vapidPublicKey: string | null = null;

  /**
   * Initialize push notifications
   */
  async initialize(): Promise<void> {
    // Check if service workers and push are supported
    if (!this.isSupported()) {
      console.log('[PushNotifications] Not supported in this browser');
      return;
    }

    try {
      // Register service worker
      await this.registerServiceWorker();

      // Get VAPID public key from backend
      await this.fetchVapidKey();

      // Check existing subscription
      await this.checkExistingSubscription();
    } catch (error) {
      console.error('[PushNotifications] Initialization error:', error);
    }
  }

  /**
   * Check if push notifications are supported
   */
  isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Get current push support status
   */
  async getStatus(): Promise<PushSupport> {
    if (!this.isSupported()) {
      return {
        supported: false,
        permission: 'default',
        subscribed: false,
      };
    }

    const permission = Notification.permission;
    const subscription = await this.getSubscription();

    const result: PushSupport = {
      supported: true,
      permission,
      subscribed: !!subscription,
    };
    if (subscription) {
      result.subscription = subscription;
    }
    return result;
  }

  /**
   * Register the service worker
   */
  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers not supported');
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      this.serviceWorker = registration;
      console.log('[PushNotifications] Service worker registered');

      // Update service worker if available
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated') {
              console.log('[PushNotifications] Service worker updated');
            }
          });
        }
      });
    } catch (error) {
      console.error('[PushNotifications] Service worker registration failed:', error);
      throw error;
    }
  }

  /**
   * Fetch VAPID public key from backend
   */
  private async fetchVapidKey(): Promise<void> {
    try {
      const response = await apiClient.get<{ data: { vapidPublicKey: string } }>(
        '/notifications/push/vapid-key'
      );

      if (response.data?.data?.vapidPublicKey) {
        this.vapidPublicKey = response.data.data.vapidPublicKey;
        console.log('[PushNotifications] VAPID key fetched');
      }
    } catch (error) {
      // Silently fail - push notifications may not be configured
      console.log('[PushNotifications] VAPID key not available (push notifications may not be configured)');
    }
  }

  /**
   * Check for existing subscription
   */
  private async checkExistingSubscription(): Promise<void> {
    if (!this.serviceWorker) {
      return;
    }

    try {
      const subscription = await this.serviceWorker.pushManager.getSubscription();
      if (subscription) {
        this.subscription = subscription;
        console.log('[PushNotifications] Existing subscription found');
      }
    } catch (error) {
      console.error('[PushNotifications] Failed to check subscription:', error);
    }
  }

  /**
   * Get current push subscription
   */
  async getSubscription(): Promise<PushSubscription | null> {
    if (!this.serviceWorker) {
      return null;
    }

    try {
      return await this.serviceWorker.pushManager.getSubscription();
    } catch (error) {
      console.error('[PushNotifications] Failed to get subscription:', error);
      return null;
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    const permission = await Notification.requestPermission();
    console.log('[PushNotifications] Permission:', permission);

    return permission;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      throw new Error('Push notifications not supported');
    }

    if (!this.serviceWorker) {
      throw new Error('Service worker not registered');
    }

    if (!this.vapidPublicKey) {
      throw new Error('Push notifications are not configured on this server');
    }

    // Request permission if needed
    const permission = Notification.permission;
    if (permission === 'default') {
      const newPermission = await this.requestPermission();
      if (newPermission !== 'granted') {
        return false;
      }
    } else if (permission === 'denied') {
      throw new Error('Notification permission denied');
    }

    try {
      // Convert VAPID key from base64 to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(this.vapidPublicKey);

      // Subscribe to push notifications
      const subscription = await this.serviceWorker.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });

      // Save subscription to backend
      await this.saveSubscription(subscription);

      this.subscription = subscription;
      console.log('[PushNotifications] Subscribed successfully');

      return true;
    } catch (error) {
      console.error('[PushNotifications] Subscription failed:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.subscription) {
      const subscription = await this.getSubscription();
      if (!subscription) {
        return false;
      }
      this.subscription = subscription;
    }

    try {
      // Unsubscribe from push service
      await this.subscription.unsubscribe();

      // Remove subscription from backend
      await this.removeSubscription(this.subscription);

      this.subscription = null;
      console.log('[PushNotifications] Unsubscribed successfully');

      return true;
    } catch (error) {
      console.error('[PushNotifications] Unsubscribe failed:', error);
      throw error;
    }
  }

  /**
   * Save subscription to backend
   */
  private async saveSubscription(subscription: PushSubscription): Promise<void> {
    const subscriptionData = subscription.toJSON();

    await apiClient.post('/notifications/push/subscribe', {
      endpoint: subscriptionData.endpoint,
      keys: {
        p256dh: subscriptionData.keys?.p256dh,
        auth: subscriptionData.keys?.auth,
      },
    });
  }

  /**
   * Remove subscription from backend
   */
  private async removeSubscription(subscription: PushSubscription): Promise<void> {
    const subscriptionData = subscription.toJSON();

    await apiClient.post('/notifications/push/unsubscribe', {
      endpoint: subscriptionData.endpoint,
    });
  }

  /**
   * Convert base64 string to Uint8Array for VAPID key
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }
}

// Export singleton instance
export const pushNotifications = new PushNotificationManager();